import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { filter, map, skip, take } from "rxjs";
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  NavigationStart,
  Router,
} from "@angular/router";
import { WissalstoreService } from "../Services/wissalstore.service";
import { CategoryService } from "../Services/category.service";
import { Category } from "../Models/category";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { AccountService } from "../Services/account.service";
import { ContactInformationsService } from "../Services/contact-informations.service";
import { ContactInformations } from "../Models/Contact-informations";
import {
  keepsStateOnLanguageChange,
  LanguageService,
} from "../Services/language.service";
import { TranslationService } from "../Services/translation.service";

@Component({
    selector: "app-main",
    templateUrl: "./main.component.html",
    styleUrls: ["./main.component.scss"],
    standalone: false
})
export class MainComponent implements OnInit, AfterViewInit {
  //for the loading bar
  loading = true;
  contactInformation: ContactInformations | undefined;
  isConnected = false;

  /** The routed page starts once the first translations are loaded, so it can translate while it initializes. */
  readonly translationsReady$ = inject(TranslationService).ready$.pipe(
    take(1),
    map(() => true)
  );

  /**
   * Changes on each language change, which re-creates the routed page: it reloads its backend data in the new
   * language. Pages with a form in progress opt out with the route data `keepOnLanguageChange: true`
   * (their texts still switch through the translate pipe).
   */
  pageKey = 0;

  private readonly destroyRef = inject(DestroyRef);

  @ViewChild("stickyHeader") stickyHeader!: ElementRef<HTMLElement>;

  categories: {
    category: Category;
    subCategories: {
      category: Category;
      subCategories: { category: Category }[];
    }[];
  }[] = [];

  constructor(
    private router: Router,
    public wissalStoreService: WissalstoreService,
    private categoryService: CategoryService,
    public accountService: AccountService,
    public breakpointObserver: BreakpointObserver,
    private contactInformationsService: ContactInformationsService,
    private languageService: LanguageService,
    private translationService: TranslationService
  ) {
    this.languageService.language$
      .pipe(
        skip(1),
        filter(() => !this.currentPageKeepsState()),
        takeUntilDestroyed()
      )
      .subscribe(() => this.reloadPage());

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.loading = true;
      } else if (event instanceof NavigationEnd) {
        this.loading = false;
      }
    });
  }

  ngOnInit(): void {
    this.breakpointObserver
      .observe([Breakpoints.Small, Breakpoints.Handset])
      .pipe(
        filter((result) => result.matches),
        take(1)
      )
      .subscribe(() => this.getMenuElements());

    this.accountService.accountSubject.subscribe((client) => {
      if (client) {
        this.isConnected = true;
      } else {
        this.isConnected = false;
      }
    });
    this.contactInformationsService
      .getContactInformations()
      .subscribe((res) => {
        this.contactInformation = res;
      });
  }

  /**
   * Keeps in-page jumps (anchors, scrollIntoView — e.g. the "Sommaire" of the general conditions) from
   * landing under the sticky header: the page's scroll-padding follows the header's height, which
   * changes with the screen size and the language.
   */
  ngAfterViewInit(): void {
    const root = document.documentElement;
    const observer = new ResizeObserver(([entry]) => {
      root.style.scrollPaddingTop = `${entry.target.getBoundingClientRect().height + 16}px`;
    });
    observer.observe(this.stickyHeader.nativeElement);
    this.destroyRef.onDestroy(() => {
      observer.disconnect();
      root.style.scrollPaddingTop = "";
    });
  }

  onClose() {
    this.wissalStoreService.sideNavOpen = false;
  }
  getMenuElements() {
    // categories are re-published by the header on each language change: rebuild the menu each time
    this.categoryService.categories
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((categories: Category[]) =>
        this.structureCategories(categories)
      );
  }

  /**
   * Re-runs the route resolvers in the new language (e.g. the product of the detail page, whose route has
   * `runGuardsAndResolvers: "always"`), then re-creates the page with their new data.
   */
  private reloadPage(): void {
    this.router
      .navigateByUrl(this.router.url, { onSameUrlNavigation: "reload" })
      .finally(() => this.pageKey++);
  }

  private currentPageKeepsState(): boolean {
    for (
      let route: ActivatedRouteSnapshot | null =
        this.router.routerState.snapshot.root;
      route;
      route = route.firstChild
    ) {
      if (keepsStateOnLanguageChange(route.data)) {
        return true;
      }
    }
    return false;
  }

  parentCategories(categories: Category[]): Category[] {
    return categories.filter(
      (category: Category) => category.parentId === null
    );
  }

  subCategories(categories: Category[], id: number): Category[] {
    return categories.filter((category: Category) => category.parentId === id);
  }

  // navigate(categoryName: string, categoryId: number) {
  //   this.router.navigate(['/browse/category', categoryName, categoryId])
  //     .then(() => this.showDropDown = false);
  // }

  structureCategories(categories: Category[]) {
    this.categories = [];

    let cat: {
      category: Category;
      subCategories: {
        category: Category;
        subCategories: { category: Category }[];
      }[];
    };

    this.parentCategories(categories).forEach((e) => {
      cat = { category: e, subCategories: [] };
      this.subCategories(categories, e.id!).forEach((sub) => {
        let subCat: { category: Category }[] = [];
        this.subCategories(categories, sub.id!).forEach((a) => {
          subCat.push({ category: a });
        });

        cat.subCategories.push({ category: sub, subCategories: subCat });
      });
      this.categories.push(cat);
    });
  }
}
